/* =========================================================================
   Wristory PDP v2 — conversion UX behaviors
   - Book a Viewing drawer
   - Condition grading modal
   - Share icons (copy / whatsapp / sms / messenger)
   ========================================================================= */

(() => {
  const focusableSelector =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ---------- Book a Viewing drawer ---------- */
  function initBookViewingDrawer(root) {
    if (root.dataset.wtBvInit === 'true') return;
    root.dataset.wtBvInit = 'true';

    // Move to body so fixed positioning doesn't get clipped by ancestors
    if (!root.dataset.movedToBody) {
      document.body.appendChild(root);
      root.dataset.movedToBody = 'true';
    }

    const panel = root.querySelector('.wt-bv-panel');
    const backdrop = root.querySelector('.wt-bv-backdrop');
    const closeButtons = Array.from(root.querySelectorAll('[data-bv-close]'));
    const id = root.getAttribute('id');

    const openers = Array.from(
      document.querySelectorAll(`[data-bv-open="${id}"], [data-bv-open=""][aria-controls="${id}"]`)
    );
    const firstField = panel?.querySelector('input:not([type="hidden"]), textarea, select');

    let lastActive = null;

    const setExpanded = (isOpen) => {
      openers.forEach((btn) => btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false'));
    };

    const open = () => {
      lastActive = document.activeElement;
      root.classList.add('is-open');
      document.body.classList.add('wt-bv-open');
      setExpanded(true);
      window.requestAnimationFrame(() => {
        panel?.focus();
        firstField?.focus();
      });
    };

    const close = () => {
      root.classList.remove('is-open');
      document.body.classList.remove('wt-bv-open');
      setExpanded(false);
      if (lastActive && typeof lastActive.focus === 'function') lastActive.focus();
    };

    openers.forEach((btn) =>
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      })
    );

    closeButtons.forEach((btn) =>
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        close();
      })
    );

    backdrop?.addEventListener('click', (e) => {
      e.preventDefault();
      close();
    });

    document.addEventListener('keydown', (e) => {
      if ((e.key === 'Escape' || e.code === 'Escape') && root.classList.contains('is-open')) {
        e.preventDefault();
        close();
      }
    });

    // If re-rendered with a form error/success state, auto-open so user sees it
    if (root.querySelector('.form-status, .form__message')) {
      open();
    }
  }

  /* ---------- Condition grading modal ---------- */
  function initGradingModal(root) {
    if (root.dataset.wtGradingInit === 'true') return;
    root.dataset.wtGradingInit = 'true';

    if (!root.dataset.movedToBody) {
      document.body.appendChild(root);
      root.dataset.movedToBody = 'true';
    }

    const closeButtons = Array.from(root.querySelectorAll('[data-grading-close]'));
    const openers = Array.from(document.querySelectorAll('[data-grading-open]'));
    const panel = root.querySelector('.wt-grading-panel');
    let lastActive = null;

    const scrollToSection = (target) => {
      if (!panel) return;
      if (!target) {
        panel.scrollTo({ top: 0, behavior: 'auto' });
        return;
      }
      const targetEl = panel.querySelector(`[data-grading-section="${target}"]`);
      if (!targetEl) {
        panel.scrollTo({ top: 0, behavior: 'auto' });
        return;
      }
      // Compute offset within the scrollable panel and scroll there.
      const panelTop = panel.getBoundingClientRect().top;
      const targetTop = targetEl.getBoundingClientRect().top;
      const delta = targetTop - panelTop;
      panel.scrollTo({ top: panel.scrollTop + delta - 16, behavior: 'smooth' });
    };

    const open = (target) => {
      lastActive = document.activeElement;
      root.classList.add('is-open');
      document.body.classList.add('wt-grading-open');
      window.requestAnimationFrame(() => {
        panel?.focus();
        // Wait one frame for layout to stabilise before scrolling.
        window.requestAnimationFrame(() => scrollToSection(target));
      });
    };

    const close = () => {
      root.classList.remove('is-open');
      document.body.classList.remove('wt-grading-open');
      if (lastActive && typeof lastActive.focus === 'function') lastActive.focus();
    };

    openers.forEach((btn) =>
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const target = btn.getAttribute('data-grading-open') || '';
        open(target);
      })
    );

    closeButtons.forEach((btn) =>
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        close();
      })
    );

    // Click on backdrop (outside panel) closes
    root.addEventListener('click', (e) => {
      if (e.target === root) close();
    });

    document.addEventListener('keydown', (e) => {
      if ((e.key === 'Escape' || e.code === 'Escape') && root.classList.contains('is-open')) {
        e.preventDefault();
        close();
      }
    });
  }

  /* ---------- Share icons ---------- */
  function initShareIcons(root) {
    if (root.dataset.wtShareInit === 'true') return;
    root.dataset.wtShareInit = 'true';

    const url = root.getAttribute('data-share-url') || window.location.href;
    const title = root.getAttribute('data-share-title') || document.title;
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const encodedMessage = encodeURIComponent(`${title} — ${url}`);
    const toast = root.querySelector('.wt-share__toast');

    const showToast = (msg) => {
      if (!toast) return;
      toast.textContent = msg;
      toast.classList.add('is-visible');
      window.setTimeout(() => toast.classList.remove('is-visible'), 1800);
    };

    root.querySelectorAll('[data-share-action]').forEach((btn) => {
      const action = btn.getAttribute('data-share-action');

      if (action === 'copy') {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          try {
            await navigator.clipboard.writeText(url);
            showToast('Link copied');
          } catch (err) {
            // Fallback: try execCommand
            const ta = document.createElement('textarea');
            ta.value = url;
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            try {
              document.execCommand('copy');
              showToast('Link copied');
            } catch (_) {
              showToast('Copy failed');
            } finally {
              document.body.removeChild(ta);
            }
          }
        });
      }

      if (action === 'whatsapp') {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          window.open(`https://wa.me/?text=${encodedMessage}`, '_blank', 'noopener');
        });
      }

      if (action === 'sms') {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          // iOS prefers `&`, Android prefers `?`. Semicolon works broadly.
          window.location.href = `sms:?&body=${encodedMessage}`;
        });
      }

      if (action === 'messenger') {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          // Use the generic share sheet on mobile if available; otherwise open Messenger web share
          if (navigator.share) {
            navigator.share({ title, url }).catch(() => {});
          } else {
            window.open(
              `https://www.facebook.com/dialog/send?link=${encodedUrl}&redirect_uri=${encodedUrl}&app_id=0`,
              '_blank',
              'noopener'
            );
          }
        });
      }
    });
  }

  /* ---------- Boot ---------- */
  function initAll() {
    document.querySelectorAll('[data-bv-drawer]').forEach(initBookViewingDrawer);
    document.querySelectorAll('[data-grading-modal]').forEach(initGradingModal);
    document.querySelectorAll('[data-wt-share]').forEach(initShareIcons);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
